import React, { useState, useEffect, useRef } from 'react';
import { Table, message, Button, Input, Space, DatePicker, Tabs, Tag, Select, Tooltip } from 'antd';
import { ReloadOutlined, SearchOutlined, BarChartOutlined, UnorderedListOutlined } from '@ant-design/icons';

import http from '../../utils/http';
import './index.scss';

const ErrorList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
  });
  const [source, setSource] = useState('');
  const [filename, setFilename] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const loadingRef = useRef(false);

  const getData = async (p = pagination.current, ps = pagination.pageSize) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const params = { page: p, pageSize: ps };
      if (source.trim()) params.source = source.trim();
      if (filename.trim()) params.filename = filename.trim();
      if (dateRange && dateRange[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
      if (dateRange && dateRange[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');

      const result = await http.get('/api/error-log/list', { params });
      if (result.success) {
        setData(result.data.list || []);
        setPagination(prev => ({ ...prev, total: result.data.total || 0 }));
      } else {
        message.error(result.message || 'Failed to fetch data');
      }
    } catch (err) {
      message.error(`Query failed: ${err.message}`);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, fixed: 'left' },
    { title: 'Source', dataIndex: 'source', key: 'source', width: 150, ellipsis: true },
    { title: 'Message', dataIndex: 'message', key: 'message', width: 150, ellipsis: true },
    { title: 'Data', dataIndex: 'data', key: 'data', width: 300, ellipsis: true },
    { title: 'Page URL', dataIndex: 'pageUrl', key: 'pageUrl', width: 250, ellipsis: true },
    {
      title: 'Log Time', dataIndex: 'logTime', key: 'logTime', width: 180,
      render: (time) => time ? new Date(time).toLocaleString() : '-',
    },
  ];

  const page = pagination.current;
  const pageSize = pagination.pageSize;

  useEffect(() => { getData(page, pageSize); }, [page, pageSize]);

  const handleTableChange = (newPagination) => setPagination(newPagination);
  const handleSearch = () => { setPagination(prev => ({ ...prev, current: 1 })); getData(1, pageSize); };
  const handleRefresh = () => { setSource(''); setFilename(''); setDateRange(null); setPagination(prev => ({ ...prev, current: 1 })); getData(1, pageSize); };
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  return (
    <div>
      <div className="error-log-header">
        <Space size="middle" wrap>
          <Input placeholder="Search by Source" value={source} onChange={(e) => setSource(e.target.value)} onKeyDown={handleKeyDown} style={{ width: 180 }} allowClear />
          <Input placeholder="Search by filename in Data" value={filename} onChange={(e) => setFilename(e.target.value)} onKeyDown={handleKeyDown} style={{ width: 220 }} allowClear />
          <DatePicker.RangePicker value={dateRange} onChange={(dates) => setDateRange(dates)} disabledDate={(current) => current && current.isAfter(new Date(), 'day')} style={{ width: 260 }} />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Search</Button>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Reset</Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="id"
        scroll={{ x: 1600 }}
        expandable={{
          expandedRowRender: (record) => (
            <div className="error-log-detail">
              {record.message && <div><strong>Message:</strong> {record.message}</div>}
              {record.data && <div><strong>Data:</strong> <pre>{record.data}</pre></div>}
              {record.pageUrl && <div><strong>Page URL:</strong> {record.pageUrl}</div>}
              {record.userAgent && <div><strong>User Agent:</strong> {record.userAgent}</div>}
            </div>
          ),
        }}
      />
    </div>
  );
};

const ErrorStatistics = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [days, setDays] = useState(7);
  const [expandedDetails, setExpandedDetails] = useState({});

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const result = await http.get('/api/error-log/statistics', { params: { days }, timeout: 30000 });
      if (result.success) {
        setData(result.data.list || []);
        setTotal(result.data.total || 0);
      } else {
        message.error(result.message || 'Failed to fetch statistics');
      }
    } catch (err) {
      message.error(`Query failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatistics(); }, [days]);

  const fetchDetail = async (record) => {
    const key = `${record.source}|${record.message}`;
    if (expandedDetails[key]) return;
    try {
      const result = await http.get('/api/error-log/detail', {
        params: { source: record.source, message: record.message, limit: 5 }
      });
      if (result.success) {
        setExpandedDetails(prev => ({ ...prev, [key]: result.data.list }));
      }
    } catch (err) {
      // silently fail
    }
  };

  const getStatusTag = (record) => {
    const lastTime = new Date(record.lastOccurrence).getTime();
    const now = Date.now();
    const hoursDiff = (now - lastTime) / (1000 * 60 * 60);
    if (hoursDiff < 24) return <Tag color="red">Active</Tag>;
    if (hoursDiff < 72) return <Tag color="orange">Recent</Tag>;
    return <Tag color="green">Inactive</Tag>;
  };

  const columns = [
    {
      title: '#', key: 'rank', width: 50,
      render: (_, __, idx) => <span style={{ fontWeight: 600, color: idx < 3 ? '#ff4d4f' : '#666' }}>{idx + 1}</span>,
    },
    {
      title: 'Source', dataIndex: 'source', key: 'source', width: 130,
      render: (v) => <Tag>{v || 'Unknown'}</Tag>,
    },
    {
      title: 'Message', dataIndex: 'message', key: 'message', width: 350,
      ellipsis: true,
      render: (v) => <Tooltip title={v}><span className="error-stat-message">{v}</span></Tooltip>,
    },
    {
      title: 'Count', dataIndex: 'count', key: 'count', width: 100,
      sorter: (a, b) => a.count - b.count,
      render: (v) => <span style={{ fontWeight: 600, color: v > 1000 ? '#ff4d4f' : v > 100 ? '#fa8c16' : '#333' }}>{v.toLocaleString()}</span>,
    },
    {
      title: 'First Seen', dataIndex: 'firstOccurrence', key: 'firstOccurrence', width: 160,
      render: (v) => v ? new Date(v).toLocaleString() : '-',
    },
    {
      title: 'Last Seen', dataIndex: 'lastOccurrence', key: 'lastOccurrence', width: 160,
      render: (v) => v ? new Date(v).toLocaleString() : '-',
    },
    {
      title: 'Status', key: 'status', width: 90,
      render: (_, record) => getStatusTag(record),
    },
  ];

  const parseData = (dataStr) => {
    if (!dataStr) return null;
    try {
      return JSON.parse(dataStr);
    } catch {
      return dataStr;
    }
  };

  return (
    <div>
      <div className="error-log-header">
        <Space size="middle">
          <span className="stat-total-label">
            Total errors in {days} days: <strong>{total.toLocaleString()}</strong>
          </span>
          <Select value={days} onChange={setDays} style={{ width: 140 }}>
            <Select.Option value={1}>Last 1 day</Select.Option>
            <Select.Option value={3}>Last 3 days</Select.Option>
            <Select.Option value={7}>Last 7 days</Select.Option>
            <Select.Option value={14}>Last 14 days</Select.Option>
            <Select.Option value={30}>Last 30 days</Select.Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchStatistics}>Refresh</Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={(r) => `${r.source}|${r.message}`}
        pagination={false}
        scroll={{ x: 1200 }}
        expandable={{
          onExpand: (expanded, record) => { if (expanded) fetchDetail(record); },
          expandedRowRender: (record) => {
            const key = `${record.source}|${record.message}`;
            const details = expandedDetails[key];
            return (
              <div className="error-stat-expand">
                <div className="error-stat-expand-header">
                  <strong>Recent Samples</strong>
                  <span className="error-stat-expand-info">
                    {record.count.toLocaleString()} total occurrences
                  </span>
                </div>
                <div className="error-stat-expand-message">
                  <strong>Message:</strong> {record.message}
                </div>
                {!details ? (
                  <span className="error-stat-loading">Loading...</span>
                ) : details.length === 0 ? (
                  <span className="error-stat-loading">No detail records found</span>
                ) : (
                  <div className="error-stat-samples">
                    {details.map((item, idx) => {
                      const parsed = parseData(item.data);
                      return (
                        <div key={item.id} className="error-stat-sample-item">
                          <div className="error-stat-sample-header">
                            <Tag color="blue">#{idx + 1}</Tag>
                            <span className="error-stat-sample-time">{item.logTime || (item.createdAt ? new Date(item.createdAt).toLocaleString() : '-')}</span>
                          </div>
                          {parsed && typeof parsed === 'object' ? (
                            <div className="error-stat-sample-data">
                              {parsed.file && <div><strong>File:</strong> <span className="error-stat-file">{parsed.file}</span></div>}
                              {parsed.line && <div><strong>Line:</strong> {parsed.line}{parsed.col ? `:${parsed.col}` : ''}</div>}
                              {parsed.stack && <div><strong>Stack:</strong> <pre className="error-stat-stack">{parsed.stack}</pre></div>}
                              {parsed.resourceUrl && <div><strong>Resource:</strong> <span className="error-stat-file">{parsed.resourceUrl}</span></div>}
                              {parsed.tagName && <div><strong>Element:</strong> &lt;{parsed.tagName}&gt;</div>}
                            </div>
                          ) : parsed ? (
                            <div className="error-stat-sample-data"><pre className="error-stat-stack">{typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2)}</pre></div>
                          ) : null}
                          {item.pageUrl && <div className="error-stat-sample-url"><strong>Page:</strong> <a href={item.pageUrl} target="_blank" rel="noreferrer">{item.pageUrl}</a></div>}
                          {item.userAgent && <div className="error-stat-sample-ua"><strong>UA:</strong> {item.userAgent}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          },
        }}
      />
    </div>
  );
};

const ErrorLog = () => {
  return (
    <div className="error-log-page">
      <Tabs
        defaultActiveKey="list"
        items={[
          {
            key: 'list',
            label: <span><UnorderedListOutlined /> Error List</span>,
            children: <ErrorList />,
          },
          {
            key: 'statistics',
            label: <span><BarChartOutlined /> Error Statistics</span>,
            children: <ErrorStatistics />,
          },
        ]}
      />
    </div>
  );
};

export default ErrorLog;
